/** What a keypress while presenting means for inserting into the focused unit. */
export type PresentInsertIntent = "entry" | "task" | null;

/** The parts of a KeyboardEvent this decision depends on. Pure so it can be tested
 * without a DOM — every guard below exists because it was a REAL bug, and inline
 * in an effect nothing could reach them with a test. */
export interface PresentKeyEvent {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** True on OS auto-repeat while the key is held down. */
  repeat: boolean;
  /** Focus is in a text surface: contentEditable, INPUT or TEXTAREA. */
  targetIsEditable: boolean;
  /**
   * Focus is on something that acts on Enter itself — a real `<button>`, a link,
   * a `<select>`, or anything wearing `role="button"` / a focusable `tabindex`.
   *
   * 🔴 A TAG list is not enough, and that was a finding: the toast is a
   * `<div tabIndex={0}>` with its own Enter handler that does not stop
   * propagation, so Enter on a focused toast both dismissed it AND wrote a stray
   * Eintrag onto the shared screen. The caller resolves this with `closest()`.
   */
  targetIsInteractive: boolean;
}

/**
 * Enter = neuer Eintrag, A = neue Aufgabe, into the focused Zwischenabschnitt.
 * Returns null when the key is not an insert.
 *
 * 🔴 Only when nothing interactive holds focus. Both keys `preventDefault()`, so
 * firing them over a focused control BOTH swallows that control's own Enter AND
 * writes a stray entry onto a screen the whole meeting is watching. The worst case
 * is the Excel paste chooser: it is `role="group"`, NOT a dialog (deliberately — a
 * portaled dialog fights the editor's focus), so a `[role="dialog"]` guard misses
 * it, and its primary button is autoFocused. Enter there must insert the table;
 * hijacking it destroys the captured clipboard along with the chooser.
 *
 * 🔴 Unmodified, non-repeating only:
 *   - Ctrl/Cmd+A must stay select-all.
 *   - A Ctrl+Enter landing a frame after the editor closed is the FINISH chord,
 *     not a second insert.
 *   - OS key-repeat on a held Enter mints an entry every ~30ms, and all but the
 *     last never mount an editor — so the ghost-row cleanup never runs on them and
 *     they become PERMANENT empty rows in the protocol.
 */
export function insertIntentForKey(e: PresentKeyEvent): PresentInsertIntent {
  if (e.targetIsEditable || e.targetIsInteractive) return null;
  if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return null;
  if (e.key === "Enter") return "entry";
  if (e.key.toLowerCase() === "a") return "task";
  return null;
}

/** Everything that acts on its own Enter. Resolved with `closest()`, not by tag,
 * so a `role="button"` div or a focusable `tabindex` (the toast) is caught too.
 *
 * 🔴 This selector ALSO matches a TipTap editor: ProseMirror carries `tabindex="0"`.
 * Anything using it to mean "a control the user clicked" must exclude editors first —
 * see `controlToBlurOnPresentClick`. */
export const INTERACTIVE_SELECTOR =
  'button, a[href], select, [role="button"], [tabindex]:not([tabindex="-1"])';

/** True when the node is inside a rich-text editor.
 *
 * Checks the ATTRIBUTE as well as `isContentEditable`: the property is inherited and correct
 * in a real browser, but jsdom does not implement it — so a unit test would silently pass
 * against a guard that is dead in production. The attribute lookup works in both. */
function isInEditor(el: HTMLElement): boolean {
  return (
    el.isContentEditable || el.closest('[contenteditable=""], [contenteditable="true"]') !== null
  );
}

/**
 * While presenting, which clicked control (if any) must be blurred?
 *
 * Chrome focuses a button on mousedown, and the insert keys (Enter / A) deliberately stand
 * down while anything interactive holds focus — so clicking the eye button, ticking a point
 * off, or hitting "Bearbeiten entsperren" silently killed the keyboard loop that is the whole
 * point of presentation mode. Blurring the clicked control hands focus back to the document.
 *
 * 🔴🔴 BUT NEVER BLUR A TEXT EDITOR. TipTap sets `tabindex="0"` on its ProseMirror div, so the
 * editor MATCHES `INTERACTIVE_SELECTOR` — it looks like a button to this rule. Clicking into
 * your own text therefore blurred the field you were writing in, and a memo entry COMMITS AND
 * CLOSES on blur (`MemoEntryRow.exitEditor`). Live, in front of the room: click a bullet to
 * edit, click again to place the caret, and it slams shut. Arrow keys were fine — no click.
 * Clicking inside the field you are writing in is not "leaving a control", it is placing a caret.
 *
 * 🔴 Two separate static reads of prosemirror-view concluded the editable div carries no
 * tabindex and so could never match. BOTH WERE WRONG; TipTap adds it. Only a real browser
 * settled it (`tests/e2e/live/memo-present-edit.spec.ts` dumps the live attributes). Do not
 * remove the editor guard on the strength of a source read.
 *
 * Pure so it can actually be tested — the same reason `insertIntentForKey` exists.
 */
export function controlToBlurOnPresentClick(opts: {
  target: HTMLElement | null;
  activeElement: Element | null;
  /** `MouseEvent.detail`. 0 ⇒ a KEYBOARD activation (Enter/Space on a focused button), which
   * also dispatches a click. Blurring there hands focus to <body>, so the NEXT Enter sees no
   * interactive target and inserts an empty Eintrag onto the shared screen — the exact failure
   * the insert guard exists to prevent, reintroduced by its own fix. */
  detail: number;
}): HTMLElement | null {
  const { target, activeElement, detail } = opts;
  if (!target || detail === 0) return null;
  // A dialog owns its own focus and its own keyboard; never reach into one. (Radix portals
  // still propagate through the React tree, so this is needed.)
  if (target.closest('[role="dialog"]')) return null;
  if (isInEditor(target)) return null;
  const control = target.closest<HTMLElement>(INTERACTIVE_SELECTOR);
  if (!control || control !== activeElement) return null;
  return control;
}
